// ─────────────────────────────────────────────────────────────────────────────
// RememberReferenceDialogComponent — asked before the registry is written
// ─────────────────────────────────────────────────────────────────────────────
// Correcting the product of an observation can register the customer's code as
// an external reference, and that write outlives the correction: the KB tier
// outranks the catalogue in the matcher, so the reference redirects every later
// match of that customer. A correction meant for one line would re-route the
// next import.
//
// So the operator answers for it, the same way the matched-order screen asks
// (`EnrichExternalReferencesDialog`): yes registers the code, no corrects this
// price alone. There is no third way out — no cross, no cancel — because both
// answers save the correction and a silent close would look like one of them.
// ─────────────────────────────────────────────────────────────────────────────

import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';

import { SharedModule } from 'src/app/shared/shared.module';
import { UiModule } from 'src/app/ui/ui.module';

export interface RememberReferenceDialogData {
  /** The code the reference would be keyed on, as the server computed it. */
  code: string;
  /** Who the reference belongs to — it is customer-scoped, always. */
  customerName: string;
  /** The product the code would point at, "SKU — Name" when both are known. */
  productLabel: string;
}

@Component({
  selector: 'app-remember-reference-dialog',
  standalone: true,
  imports: [CommonModule, TranslateModule, SharedModule, UiModule],
  templateUrl: './remember-reference-dialog.component.html',
  styleUrls: ['./remember-reference-dialog.component.scss'],
})
export class RememberReferenceDialogComponent {
  constructor(
    private dialogRef: MatDialogRef<RememberReferenceDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: RememberReferenceDialogData,
  ) {}

  /** Save the correction and register the code. */
  remember(): void {
    this.dialogRef.close(true);
  }

  /** Save the correction alone — the matcher learns nothing. */
  correctOnly(): void {
    this.dialogRef.close(false);
  }
}
